import api from './api'

const accessTokenKey = 'budget-quotidien-access-token'
const refreshTokenKey = 'budget-quotidien-refresh-token'
const hemocultureTokenKey = 'hemoculture-token'

const getAccessToken = () => localStorage.getItem(hemocultureTokenKey) || localStorage.getItem(accessTokenKey)
const getRefreshToken = () => localStorage.getItem(refreshTokenKey)
const setAccessToken = (token: string) => localStorage.setItem(accessTokenKey, token)
const clearTokens = () => {
  localStorage.removeItem(accessTokenKey)
  localStorage.removeItem(refreshTokenKey)
}

let refreshPromise: Promise<string | null> | null = null

const shouldAttemptRefresh = (input: RequestInfo | URL) => {
  const url = String(input)
  return !url.includes('/auth/login/') && !url.includes('/auth/refresh/')
}

const refreshAccessToken = async (): Promise<string | null> => {
  const refresh = getRefreshToken()
  if (!refresh) return null

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await fetch(api.authRefresh(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh }),
        })

        if (!response.ok) {
          clearTokens()
          return null
        }

        const payload = (await response.json()) as { access?: string }
        if (!payload?.access) {
          clearTokens()
          return null
        }

        setAccessToken(payload.access)
        return payload.access
      } catch {
        clearTokens()
        return null
      } finally {
        refreshPromise = null
      }
    })()
  }

  return refreshPromise
}

export class HttpError extends Error {
  status: number
  payload?: unknown

  constructor(message: string, status: number, payload?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.payload = payload
  }
}

export const http = async <T>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> => {
  const executeRequest = async (overrideToken?: string) => {
    const headers = new Headers(init.headers || {})

    if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json')
    }

    const token = overrideToken || getAccessToken()

    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    return fetch(input, {
      ...init,
      headers,
    })
  }

  let response = await executeRequest()

  if (response.status === 401 && shouldAttemptRefresh(input)) {
    const refreshedAccess = await refreshAccessToken()
    if (refreshedAccess) {
      response = await executeRequest(refreshedAccess)
    }
  }

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || ''
    let payload: unknown
    let message = `Request failed with status ${response.status}`

    if (contentType.includes('application/json')) {
      payload = await response.json()
      if (payload && typeof payload === 'object') {
        const firstEntry = Object.entries(payload as Record<string, unknown>)[0]
        if (firstEntry) {
          const [, value] = firstEntry
          if (typeof value === 'string') {
            message = value
          } else if (Array.isArray(value) && typeof value[0] === 'string') {
            message = value[0]
          }
        }
      }
    } else {
      const errorText = (await response.text()).trim()
      payload = errorText
      // Do not bubble raw HTML in user-facing errors.
      if (errorText && !errorText.toLowerCase().includes('<html')) {
        message = errorText
      }
    }

    throw new HttpError(message, response.status, payload)
  }

  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}
